'use client';

import { useEffect, useRef, useState } from 'react';

import { ArrowDown, ArrowUp, CheckIcon, ChevronDownIcon, ChevronUpIcon, RotateCcwIcon } from 'lucide-react';

import { Link } from '~/components/common/link';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

import { GameIntro } from '../../_components/game-intro';
import GameResultDialog from '../../_components/game-result-dialog';

type Attempt = {
  guess: number[];
  feedback: string[];
};

function createEmptyGuess() {
  return [0, 0, 0, 0];
}

export default function CodeCrackerGame() {
  const [targetCode, setTargetCode] = useState<number[]>([]);
  const [currentGuess, setCurrentGuess] = useState<number[]>(createEmptyGuess);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [gameWon, setGameWon] = useState(false);
  const attemptsScrollRef = useRef<HTMLDivElement>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    generateNewCode();
  }, []);

  useEffect(() => {
    const attemptsContainer = attemptsScrollRef.current;
    if (!attemptsContainer || attempts.length === 0) return;

    attemptsContainer.scrollTo({
      top: attemptsContainer.scrollHeight,
      behavior: 'smooth',
    });
  }, [attempts]);

  const generateNewCode = () => {
    const newCode = Array.from({ length: 4 }, () => Math.floor(Math.random() * 10));
    setTargetCode(newCode);
    setCurrentGuess(createEmptyGuess());
    setAttempts([]);
    setGameWon(false);
    setIsDialogOpen(false);
  };

  const submitGuess = () => {
    if (gameWon || targetCode.length === 0) return;

    const guess = [...currentGuess];
    const feedback = getFeedback(guess, targetCode);

    setAttempts((prev) => [...prev, { guess, feedback }]);

    if (feedback.every((f) => f === 'correct')) {
      setGameWon(true);

      setTimeout(() => {
        setIsDialogOpen(true);
      }, 1000);
    }
  };

  const adjustDigit = (index: number, direction: 'up' | 'down') => {
    const newGuess = [...currentGuess];
    if (direction === 'up') {
      newGuess[index] = (newGuess[index] + 1) % 10;
    } else {
      newGuess[index] = newGuess[index] === 0 ? 9 : newGuess[index] - 1;
    }
    setCurrentGuess(newGuess);
  };

  const getFeedback = (guess: number[], target: number[]) => {
    return guess.map((guessDigit, index) => {
      const targetDigit = target[index];
      if (guessDigit === targetDigit) {
        return 'correct';
      } else if (guessDigit < targetDigit) {
        return 'too-low';
      } else {
        return 'too-high';
      }
    });
  };

  const getFeedbackIcon = (feedback: string) => {
    switch (feedback) {
      case 'correct':
        return <CheckIcon className='text-teal size-4' />;
      case 'too-high':
        return <ArrowDown className='text-magenta size-4' />;
      case 'too-low':
        return <ArrowUp className='text-blue size-4' />;
      default:
        return null;
    }
  };

  const getFeedbackColor = (feedback: string) => {
    switch (feedback) {
      case 'correct':
        return 'bg-teal/10 border-teal text-teal';
      case 'too-high':
        return 'bg-magenta/10 border-magenta text-magenta';
      case 'too-low':
        return 'bg-blue/10 border-blue text-blue';
      default:
        return 'bg-black/10 border-black text-black';
    }
  };

  const hasAttempts = attempts.length > 0;

  return (
    <>
      <div
        className='sm:max-h-148 min-h-116 flex flex-col'
        style={{
          height: `calc(100svh - var(--game-layout-header-height) - (var(--game-layout-main-padding) * 2) - (var(--game-card-padding) * 2))`,
        }}
      >
        <GameIntro
          title='Can You Crack the Code?'
          description='Pick 4 digits below, then look for clues to crack the code.'
          className='mb-4 sm:mb-4'
        />
        <div className='text-asphalt-75 mb-5 flex justify-center gap-x-6 text-sm sm:gap-x-8'>
          <div className='flex items-center gap-x-2'>
            <div className='bg-teal/10 flex size-5 items-center justify-center rounded'>
              <CheckIcon className='text-teal size-3' strokeWidth={3} />
            </div>
            Correct
          </div>

          <div className='flex items-center gap-x-2'>
            <div className='bg-blue/10 flex size-5 items-center justify-center rounded'>
              <ArrowUp className='text-blue size-3' strokeWidth={3} />
            </div>
            Too Low
          </div>

          <div className='flex items-center gap-x-2'>
            <div className='bg-magenta/10 flex size-5 items-center justify-center rounded'>
              <ArrowDown className='text-magenta size-3' strokeWidth={3} />
            </div>
            Too High
          </div>
        </div>
        {/* Previous Attempts */}
        <div
          className={cn(
            'relative flex grow flex-col justify-end overflow-hidden',
            'min-[352px]:bg-size-[312px_64px] min-[352px]:bg-[url("/grid.svg")] min-[352px]:bg-bottom  min-[352px]:bg-repeat-y ',
            'before:bg-linear-to-b before:absolute before:inset-x-0 before:top-0 before:z-50 before:h-8 before:from-white',
          )}
        >
          <div
            ref={attemptsScrollRef}
            className={cn(
              hasAttempts && 'hide-scrollbar max-h-full space-y-2 overflow-y-auto overscroll-none bg-white py-2',
            )}
          >
            {attempts.map((attempt, attemptIndex) => (
              <div key={attemptIndex} className='flex justify-center gap-x-2'>
                {attempt.guess.map((digit, digitIndex) => (
                  <div
                    key={digitIndex}
                    className={cn(
                      'font-greycliff-extrabold w-18 flex h-14 items-center justify-center gap-x-2 rounded-lg border text-2xl',
                      getFeedbackColor(attempt.feedback[digitIndex]),
                    )}
                  >
                    {digit}
                    {getFeedbackIcon(attempt.feedback[digitIndex])}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className='border-asphalt-10 -mx-(--game-card-padding) -mb-(--game-card-padding) mt-4 shrink-0 px-5 pb-4 pt-1 max-sm:rounded-2xl max-sm:border sm:border-t sm:pb-8 sm:pt-3 lg:px-12'
          style={{
            background: 'linear-gradient(102deg, #E4E4E4 0%, #FFF 32.56%, #EAEAEA 63.67%, #FFF 101.1%)',
          }}
        >
          <div className='flex justify-center space-x-4'>
            {currentGuess.map((digit, index) => (
              <div key={index} className='w-18 flex flex-col items-center '>
                <button
                  type='button'
                  aria-label={`Increase digit ${index + 1}`}
                  onClick={() => adjustDigit(index, 'up')}
                  className='h-9 w-full'
                >
                  <ChevronUpIcon
                    className='stroke-asphalt-50 hover:stroke-asphalt-75 mx-auto size-5'
                    strokeWidth={2.5}
                  />
                </button>
                <div className='font-greycliff-extrabold w-full text-center text-2xl sm:text-3xl'>{digit}</div>
                <button
                  type='button'
                  aria-label={`Decrease digit ${index + 1}`}
                  onClick={() => adjustDigit(index, 'down')}
                  className='-mt-1 h-9 w-full'
                >
                  <ChevronDownIcon
                    className='stroke-asphalt-50 hover:stroke-asphalt-75 mx-auto size-5'
                    strokeWidth={2.5}
                  />
                </button>
              </div>
            ))}
          </div>

          <Button variant='secondary' onClick={submitGuess} className='mt-2 w-full' disabled={gameWon}>
            Try This Code!
          </Button>
        </div>
      </div>

      <GameResultDialog
        open={isDialogOpen}
        setOpen={setIsDialogOpen}
        title={`Congratulations! You cracked the code in ${attempts.length} tries!`}
        showConfetti={true}
      >
        <div className='text-asphalt-75 mx-auto mb-4 flex items-center justify-center gap-x-3 max-md:text-sm'>
          <hr className='border-asphalt-10 w-5' />
          The code was
          <hr className='border-asphalt-10 w-5' />
        </div>
        <div className='mb-6 flex justify-center gap-x-2 md:mb-8'>
          {targetCode.map((digit, index) => (
            <div
              key={index}
              className='bg-teal/10 border-teal/25 font-greycliff-extrabold text-teal w-13 flex aspect-square justify-center rounded-lg border pt-2.5 text-2xl md:w-14 md:text-3xl'
            >
              {digit}
            </div>
          ))}
        </div>
        <div className='flex flex-col gap-y-3'>
          <Button onClick={generateNewCode}>
            <RotateCcwIcon />
            Play Again
          </Button>

          <Button variant='outline-secondary' asChild>
            <Link href='/games'>More Games</Link>
          </Button>
        </div>
      </GameResultDialog>
    </>
  );
}
